import { useEffect } from 'react';

/**
 * Cursor-following spotlight for dark surface cards. For every card matching
 * SELECTOR it tracks the pointer and writes `--mx`/`--my` (the cursor position
 * as a percentage of the card), which a `::before` radial-gradient in
 * taranaSite.css renders as a soft glow that follows the mouse. (No per-card
 * tilt — the trio/how cards are seamless connected strips, where tilting an
 * individual card would break the unified-panel look.)
 *
 * Re-runs on `key` change (pass the route pathname) so cards on freshly mounted
 * pages get wired up. No-ops under prefers-reduced-motion and on touch / coarse
 * pointers, where a hover spotlight makes no sense.
 */
const SELECTOR = '.trio-card, .how-step, .fcard, .backer-tile, .team-card';

export function useCardSpotlight(key?: string) {
  useEffect(() => {
    const root = document.querySelector('.tarana-site');
    if (!root) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (reduceMotion || !finePointer) return;

    const cards = Array.from(root.querySelectorAll<HTMLElement>(SELECTOR));
    if (cards.length === 0) return;

    const cleanups: Array<() => void> = [];

    cards.forEach((card) => {
      card.classList.add('has-spotlight');
      let raf = 0;

      const onMove = (e: MouseEvent) => {
        const r = card.getBoundingClientRect();
        const mx = ((e.clientX - r.left) / r.width) * 100;
        const my = ((e.clientY - r.top) / r.height) * 100;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          card.style.setProperty('--mx', `${mx}%`);
          card.style.setProperty('--my', `${my}%`);
        });
      };

      card.addEventListener('mousemove', onMove);
      cleanups.push(() => {
        cancelAnimationFrame(raf);
        card.removeEventListener('mousemove', onMove);
        card.classList.remove('has-spotlight');
      });
    });

    return () => cleanups.forEach((fn) => fn());
  }, [key]);
}
